import { IUserInteractiveComponent, isNullOrUndefined } from '@adaptiva/core/common';
import { Vector2 } from '@adaptiva/core/space';
import { BaseWidgetComponent, WidgetService } from '@adaptiva/core/widgets';
import { InputComponent } from '@adaptiva/widgets/input';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, forwardRef } from '@angular/core';

@Component({
	standalone: false,
	selector: 'ada-vector2',
	templateUrl: './vector2.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		WidgetService,
		{
			provide: BaseWidgetComponent,
			useExisting: forwardRef(() => Vector2Component)
		}
	]
})
export class Vector2Component extends BaseWidgetComponent implements IUserInteractiveComponent<Vector2>, AfterViewInit {

	@Input()
	public max?: Vector2;

	@Input()
	public min?: Vector2;

	@Input()
	public xLabel: string = 'x';

	@Input()
	public xLabelInfo?: string;

	@Input()
	public yLabel: string = 'y';

	@Input()
	public yLabelInfo?: string;

	@Input()
	public set value(_value: Vector2) {
		this.setValue(_value);
	}

	@Output()
	public valueChange: EventEmitter<Vector2> = new EventEmitter();

	@ViewChild('XInput')
	protected xInput: InputComponent;
	
	@ViewChild('YInput')
	protected yInput: InputComponent;

	private _value: Vector2 = Vector2.null();

	public get value(): Vector2 {
		return this._value;
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();

		if (isNullOrUndefined(this.value.x))
			this.xInput?.clear();

		if (isNullOrUndefined(this.value.y))
			this.yInput?.clear();
	}

	public setValue(_value: Vector2, _emit: boolean = false): void {
		this._value = this.clamp(_value);
		this.detectChanges();

		if (_emit)
			this.valueChange.emit(this._value);
	}

	public setX(x: float): void {
		x = +x;

		if (x === this.value.x)
			return;

		this._value.x = x;
		this.emit();
	}

	public clearX(): void {
		this._value.x = null;
		this.xInput.clear();
		this.valueChange.emit(this._value);
	}

	public setY(y: float): void {
		y = +y;

		if (y === this.value.y)
			return;

		this._value.y = y;
		this.emit();
	}

	public clearY(): void {
		this._value.y = null;
		this.yInput.clear();
		this.valueChange.emit(this._value);
	}

	protected emit(): void {
		this._value = this.clamp(this._value);
		this.valueChange.emit(this._value);
	}

	protected isValueInRange(): boolean {
		if (this.min.x > +this.value.x || +this.value.x > this.max.x)
			return false;

		if (this.min.y > +this.value.y || +this.value.y > this.max.y)
			return false;

		return true;
	}

	protected clamp(_vector2: Vector2): Vector2 {
		if (this.min && this.max && !this.isValueInRange())
			return Vector2.clamp(_vector2, this.min, this.max);

		return _vector2;
	}

}
